/* eslint-disable react-hooks/exhaustive-deps */
import { BookingProvider, Path, Signal, StoreKey } from "@common/constans";
import { SearchMode, StorageType } from "@common/enum";
import { CabinClass, IAvailabilityRequest, IOriginDestinationRequest, IPaxTypeQuantity, OriginDestionationRequest, TripType } from "@common/models";
import ClassOfService from "@components/flightSearch/ClassOfService";
import FlightTripItem from "@components/flightSearch/FlightTripItem";
import Alert from "@components/general/Alert";
import Button from "@components/general/form/Button";
import RadioInput from "@components/general/form/RadioInput";
import Panel from "@components/general/Panel";
import PaxType from "@components/general/PaxType";
import { CreateStep } from "@components/Stepper";
import { IViewport } from "@config/appConfig";
import { Culture, IValidationErrors } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { store } from "@misc/Reducer";
import { IAppState } from "@misc/State";
import Abort from "@service/Abort";
import Config from "@service/Config";
import Storage from "@service/Storage";
import Translate from "@service/Translate";
import { formatToDateTime, setToLocaleDawn } from "@utilize/Format";
import { clearStoreForSearch, getAdditionalDate, nameof } from "@utilize/Util";
import { FC, Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";

interface IFlightSearchProps {
    tabPanelMode?: boolean
}

const FlightSearch: FC<IFlightSearchProps> = ({ tabPanelMode }) => {
    const { tr } = Translate;
    const { lang } = Culture.getLocale();
    const { small } = Config.get<IViewport>("viewport");
    const dispatch = useDispatch();
    const { push, location } = useHistory();
    const { availabilityRequest, rangeOptions, searchMode, width } = useSelector((state: IAppState) => ({
        availabilityRequest: state.availabilityRequest,
        rangeOptions: state.rangeOptions,
        searchMode: state.searchMode,
        width: state.windowWidth
    }));

    useEffect(() => {
        if (location.pathname === Path.Booking.Home)
            clearStoreForSearch(SearchMode.Create, false);
    }, []);

    useEffect(() => {
        dispatch<IAppAction>({
            type: AppAction.RouteOptions, payload: {
                routeOptions: {
                    destination: {
                        label: tr("destination"),
                        name: nameof<IOriginDestinationRequest>("destinationCode"),
                        placeholder: width > small ? tr("enter_destination") : tr("search_city_airport")
                    },
                    origin: {
                        label: tr("origin"),
                        name: nameof<IOriginDestinationRequest>("originCode"),
                        placeholder: width > small ? tr("enter_origin") : tr("search_city_airport")
                    }
                }
            }
        });
        dispatch<IAppAction>({
            type: AppAction.RangeOptions, payload: {
                rangeOptions: {
                    arrival: {
                        disable: availabilityRequest.tripType === TripType.RoundTrip ? false : true,
                        name: "arrivalDate",
                        label: tr("return_date"),
                        placeholder: tr("return_date"),
                        icon: "icon-airplane-takeoff"
                    },
                    departure: {
                        disable: false,
                        name: "departureDate",
                        label: availabilityRequest.tripType === TripType.RoundTrip ? tr("departure_date") : tr("flight_date"),
                        placeholder: availabilityRequest.tripType === TripType.RoundTrip ? tr("departure_date") : tr("flight_date"),
                        icon: 'icon-airplane-takeoff'
                    }
                },
            }
        });
    }, [availabilityRequest]);

    const onFlightTypeChange = (name: string, value: any) => {
        availabilityRequest.tripType = value;
        if (availabilityRequest.tripType === TripType.OneWay || availabilityRequest.tripType === TripType.MultiCity) {
            rangeOptions.arrival.disable = true;
            rangeOptions.departure.placeholder = tr("flight_date");
            rangeOptions.departure.label = tr("flight_date");
        } else {
            rangeOptions.arrival.disable = false;
            rangeOptions.departure.placeholder = tr("departure_date");
            rangeOptions.departure.label = tr("departure_date");
        }

        if (availabilityRequest.originDestionations) {
            const originDestionation = availabilityRequest.originDestionations[0];
            delete originDestionation.arrivalDate;
            availabilityRequest.originDestionations = availabilityRequest.tripType === TripType.MultiCity ? [{}, {}] : [originDestionation];
        }

        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
        dispatch<IAppAction>({ type: AppAction.RangeOptions, payload: { rangeOptions } });
        dispatch<IAppAction>({ type: AppAction.SelectedFlights, payload: { selectedFlights: [] } });
        dispatch<IAppAction>({ type: AppAction.ValidationErrors, payload: { validationErrors: [] } });
    }

    const addTrip = () => {
        if (availabilityRequest.originDestionations)
            availabilityRequest.originDestionations.push({});
        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
    }

    const onPaxTypeQuantityChange = (name: string, paxType: IPaxTypeQuantity) => {
        availabilityRequest.paxTypeQuantity = paxType;
        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
    }

    const redirectToAccelAero = () => {
        window.location.href = `https://reservations.mahan.aero/service-app/ibe/reservation.html#/multi-city/${lang}/IRR/IR`
    }

    const onFlightSearchBtnClick = () => {
        const url = onFlightSearch(availabilityRequest);
        url && push(url)
    }

    const tripsLength = availabilityRequest.originDestionations?.length || 0;
    const flightSearchBtn = (
        <Button
            id="search"
            className="search-btn"
            variant="contained"
            size="large"
            color="secondary"
            text={tr("search")}
            onClick={onFlightSearchBtnClick}
        />
    );

    return (
        <div className={`flight-search${tabPanelMode ? " tab-panel-mode" : ""}`}>
            <Panel color="teal">
                {
                    Config.config.setting.bookingDisableUntil && new Date(Config.config.setting.bookingDisableUntil).getTime() > new Date().getTime() ?
                        <Alert
                            title={<div dangerouslySetInnerHTML={{ __html: Config.config.setting.bookingDisableMessage?.html ?? "" }} />}
                            type="information"
                            variant="contain"
                        />
                        :
                        <>
                            <div className="flight-search-type flex flex-wrap">
                                <RadioInput
                                    name="tripType"
                                    id={TripType.OneWay.toString()}
                                    value={TripType.OneWay}
                                    label={tr("one_way")}
                                    checked={availabilityRequest.tripType === TripType.OneWay}
                                    onChange={onFlightTypeChange}
                                />
                                <RadioInput
                                    name="tripType"
                                    id={TripType.RoundTrip.toString()}
                                    value={TripType.RoundTrip}
                                    label={tr("round_trip")}
                                    checked={availabilityRequest.tripType === TripType.RoundTrip}
                                    onChange={onFlightTypeChange}
                                />
                                <RadioInput
                                    name="tripType"
                                    id={TripType.MultiCity.toString()}
                                    value={TripType.MultiCity}
                                    label={tr("multi_city")}
                                    checked={availabilityRequest.tripType === TripType.MultiCity}
                                    onChange={Config.config.defaultBookingProvider === BookingProvider.Mahan ? onFlightTypeChange : redirectToAccelAero}
                                />
                            </div>
                            {
                                availabilityRequest.tripType === TripType.MultiCity ?
                                    <Fragment>
                                        {
                                            availabilityRequest?.originDestionations?.map((item: IOriginDestinationRequest, index: number) =>
                                                <div key={index} className="form-search">
                                                    <FlightTripItem
                                                        index={index}
                                                        trip={item}
                                                        showRemoveIcon={tripsLength > 2}
                                                    />
                                                    {index < tripsLength - 1 ? <div className="flight-row-divider" /> : ""}
                                                </div>
                                            )
                                        }
                                        <div className="row flex form-search spa-1">
                                            <div className="col-3-5 col-md-1-2 col-sm-1-1">
                                                {
                                                    tripsLength < 5 ?
                                                        <div className="add-trip-btn flex">
                                                            <Button
                                                                name="addFlight"
                                                                variant="contained"
                                                                color="primary"
                                                                icon="icon-plus"
                                                                height="40px"
                                                                text={tr("flight_add")}
                                                                onClick={addTrip}
                                                            />
                                                        </div> : ""
                                                }
                                            </div>
                                            <div className="col-2-5 col-md-1-2 col-sm-1-1">
                                                <div className="row flex form-search spa-1 flex-no-wrap">
                                                    <div className="col-1-2">
                                                        <PaxType
                                                            name="paxTypeQuantity"
                                                            paxType={availabilityRequest.paxTypeQuantity!}
                                                            onChange={onPaxTypeQuantityChange}
                                                            disabled={searchMode === SearchMode.Modify}
                                                        />
                                                    </div>
                                                    <div className="col-1-2 search-btn">
                                                        {flightSearchBtn}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Fragment>
                                    :
                                    <div className={`row flex form-search spa-1${Config.config.defaultBookingProvider === BookingProvider.AccelAero ? " booking-provider-acc-mode" : ""}`}>
                                        <div className="col-14-20 col-lg-1-1 col-md-1-1 col-sm-1-1">
                                            <FlightTripItem
                                                index={0}
                                                trip={availabilityRequest.originDestionations?.length ? availabilityRequest.originDestionations[0] : {} as IOriginDestinationRequest}
                                                showRemoveIcon={false}
                                                onFlightTypeChange={onFlightTypeChange}
                                            />
                                        </div>
                                        <div className={`col-3-20 ${Config.config.defaultBookingProvider === BookingProvider.AccelAero ? "col-lg-1-3 col-md-1-3" : "col-lg-1-2 col-md-1-2"} col-sm-1-2`}>
                                            <PaxType
                                                name="paxTypeQuantity"
                                                paxType={availabilityRequest.paxTypeQuantity!}
                                                onChange={onPaxTypeQuantityChange}
                                                disabled={searchMode === SearchMode.Modify}
                                            />
                                        </div>
                                        {
                                            // add economy class for accelAero booking search
                                            Config.config.defaultBookingProvider === BookingProvider.AccelAero ?
                                                <ClassOfService
                                                    value={availabilityRequest?.originDestionations?.length && availabilityRequest.originDestionations[0].classOfService}
                                                    onChange={(name, value) => {
                                                        if (availabilityRequest.originDestionations?.length)
                                                            availabilityRequest.originDestionations[0][name as keyof IOriginDestinationRequest] = value;
                                                        dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest } });
                                                    }}
                                                /> : ""
                                        }
                                        <div className={`col-3-20 ${Config.config.defaultBookingProvider === BookingProvider.AccelAero ? "col-lg-1-3 col-md-1-3 col-sm-1-1" : "col-lg-1-2 col-md-1-2 col-sm-1-2"}`}>
                                            {flightSearchBtn}
                                        </div>
                                    </div>
                            }
                        </>
                }
            </Panel>
        </div>
    );
}

export default FlightSearch;

export const createUrlSearchParams = (availabilityRequest: IAvailabilityRequest): string => {
    let route = "", pax = "", departureDate = "", returnDate = 0, url = "";
    if (availabilityRequest.originDestionations) {
        pax = `${availabilityRequest.paxTypeQuantity?.adt},${availabilityRequest.paxTypeQuantity?.chd || 0},${availabilityRequest.paxTypeQuantity?.inf || 0}`;
        if (availabilityRequest.tripType !== TripType.MultiCity) {
            route = `${availabilityRequest.originDestionations[0].originCode}-${availabilityRequest.originDestionations[0].destinationCode}`;
            departureDate = (availabilityRequest.originDestionations[0].departureDate || 0).toString();
            returnDate = availabilityRequest.tripType === TripType.RoundTrip ? availabilityRequest.originDestionations[1].departureDate || 0 : 0;
        } else {
            availabilityRequest.originDestionations.forEach((originDestionation, index) => {
                route += `${index ? "," : ""}${originDestionation.originCode}-${originDestionation.destinationCode}`;
                departureDate += `${index ? "," : ""}${originDestionation.departureDate}`;
            })
        }
    }
    url = `${Path.Booking.SelectFlight}/${route}?pax=${pax}&tripType=${availabilityRequest.tripType}&departureDate=${departureDate}${returnDate ? `&returnDate=${returnDate}` : ''}`;
    Storage.set(StoreKey.SearchHistory, url, StorageType.Session);
    return url;
}

export const initAvailabilityRequest = (availabilityRequest: IAvailabilityRequest) => {
    const additionalDate = getAdditionalDate();
    if (availabilityRequest.originDestionations) {
        if (availabilityRequest.tripType === TripType.RoundTrip) {
            if (availabilityRequest.originDestionations.length === 2)
                availabilityRequest.originDestionations.splice(1, 1);
            const originDestination = availabilityRequest.originDestionations[0];
            availabilityRequest.originDestionations.push({
                departureDate: originDestination.arrivalDate,
                destinationCode: originDestination.originCode,
                originCode: originDestination.destinationCode,
                classOfService: originDestination.classOfService
            });
        }
        availabilityRequest.originDestionations.forEach(item => {
            item.departureDate = setToLocaleDawn(item.departureDate);
            item.daysAfter = additionalDate;
            item.daysBefore = additionalDate;
        });
    }
}

export const fetchAvailabilityRequest = (availabilityRequest: IAvailabilityRequest) => {
    if (Config.config.defaultBookingProvider === BookingProvider.AccelAero) {
        let url = "";
        const { lang } = Culture.getLocale();
        if (availabilityRequest.originDestionations?.length) {
            const originDestination = availabilityRequest.originDestionations[0];
            const depDate = formatToDateTime(originDestination.departureDate || 0, { format: "d-m-Y", locale: "en-US", utc: false });
            const arrDate = formatToDateTime(originDestination.arrivalDate || 0, { format: "d-m-Y", locale: "en-US", utc: false });
            if (availabilityRequest.tripType === TripType.RoundTrip) {
                url = `https://reservations.mahan.aero/service-app/ibe/reservation.html#/fare/${lang}/IRR/IR/${originDestination.originCode}/${originDestination.destinationCode}/${depDate}/${arrDate}/${availabilityRequest.paxTypeQuantity?.adt}/${availabilityRequest.paxTypeQuantity?.chd || 0}/${availabilityRequest.paxTypeQuantity?.inf || 0}/${CabinClass[originDestination.classOfService || 0]}/`
            } else if (availabilityRequest.tripType === TripType.OneWay) {
                url = `https://reservations.mahan.aero/service-app/ibe/reservation.html#/fare/${lang}/IRR/IR/${originDestination.originCode}/${originDestination.destinationCode}/${depDate}/N/${availabilityRequest.paxTypeQuantity?.adt}/${availabilityRequest.paxTypeQuantity?.chd || 0}/${availabilityRequest.paxTypeQuantity?.inf || 0}/${CabinClass[originDestination.classOfService || 0]}/`
            }
        }
        window.location.href = url;
    } else {
        const searchMode = store.getState().searchMode;
        Abort.get(Signal.TotalPrice).abort();
        clearStoreForSearch(searchMode, false);
        delete availabilityRequest.id;
        const paxTypeQuantity = JSON.parse(JSON.stringify(availabilityRequest.paxTypeQuantity));
        store.dispatch<IAppAction>({ type: AppAction.AvailabilityPaxTypeQuantity, payload: { availabilityPaxTypeQuantity: paxTypeQuantity } });
        store.dispatch<IAppAction>({ type: AppAction.AvailabilityRequest, payload: { availabilityRequest: { ...availabilityRequest } } });
        store.dispatch<IAppAction>({ type: AppAction.InactiveSearchPanel, payload: { inactiveSearchPanel: true } });
        store.dispatch<IAppAction>({ type: AppAction.CurrentStep, payload: { currentStep: CreateStep.SelectFlight } });
    }
}

export const validateAvailabilityRequest = (availabilityRequest: IAvailabilityRequest) => {
    const errors: IValidationErrors = [];
    const originDestionations = availabilityRequest.originDestionations;
    if (originDestionations) {

        for (let i = 0, il = originDestionations.length; i < il; i++) {
            const originDestionation = new OriginDestionationRequest(originDestionations[i]);
            let tripValidationErrors = originDestionation.validate("originCode", "destinationCode", "departureDate") || {};
            if (Config.config.defaultBookingProvider === BookingProvider.AccelAero)
                tripValidationErrors = originDestionation.validate("originCode", "destinationCode", "departureDate", "classOfService") || {};

            if (availabilityRequest.tripType === TripType.MultiCity && i > 0) {
                if (originDestionations[i - 1].departureDate! > originDestionations[i].departureDate!) {
                    tripValidationErrors.departureDate = "multicitySequence";
                }
            }

            if (Object.keys(tripValidationErrors).length) {
                errors[i] = tripValidationErrors;
            }
        }

        if (availabilityRequest.tripType === TripType.RoundTrip) {
            if (errors[1] && errors[1].departureDate) {
                errors[0] = errors[0] ?? {};
                errors[0].arrivalDate = errors[1].departureDate;
                delete errors[1];
            }

            if (originDestionations[0].departureDate && originDestionations[0].arrivalDate && originDestionations[0].departureDate > originDestionations[0].arrivalDate) {
                errors[0] = errors[0] ?? {};
                errors[0].arrivalDate = "outboundDateAfterInbound";
            }
        }
    }
    return errors;
}

export const onFlightSearch = (availabilityRequest: IAvailabilityRequest): string => {
    initAvailabilityRequest(availabilityRequest);
    let errors = validateAvailabilityRequest(availabilityRequest);
    if (Object.keys(errors).length) {
        store.dispatch<IAppAction>({ type: AppAction.ValidationErrors, payload: { validationErrors: errors } });
        return "";
    }
    fetchAvailabilityRequest(availabilityRequest);
    if (Config.config.defaultBookingProvider === BookingProvider.Mahan)
        return createUrlSearchParams(availabilityRequest);
    return "";

}
